import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import PackPreviewModal from "@/components/shared/PackPreviewModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  FileBarChart, CheckCircle2, AlertTriangle, Clock, Lock,
  Download, Send, Eye, Truck, Shield, Printer, FileText,
  Sparkles, Calendar, User, Building2, ArrowRight, Zap,
  Package, Target, Activity
} from "lucide-react";

function DossierSection({ title, icon: Icon, children, badge }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`dossier-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="bg-slate-50 px-5 py-3 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        </div>
        {badge}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function ReadinessDossier() {
  const { projects, conditions, evidence, blockers, trades, gates, workspace, calendarEvents, workProofs } = useDemoState();
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedProject, setSelectedProject] = useState('proj-001');

  const proj = projects.find(p => p.id === selectedProject);
  const projConditions = conditions.filter(c => c.projectId === selectedProject && c.stage === 'Pre-Slab');
  const projEvidence = evidence.filter(e => e.projectId === selectedProject);
  const projBlockers = blockers.filter(b => b.projectId === selectedProject);
  const projTrades = trades.filter(t => t.projectId === selectedProject);
  const gate = gates.find(g => g.projectId === selectedProject && g.stage === 'Pre-Slab');
  const projCalendar = calendarEvents.filter(e => e.projectId === selectedProject);

  const approvedEvidence = projEvidence.filter(e => e.status === 'approved');
  const pendingEvidence = projEvidence.filter(e => e.status === 'needs-review' || e.status === 'pending-match');
  const clearedCount = projConditions.filter(c => c.status === 'cleared').length;
  const totalCount = projConditions.length;
  const readinessPercent = totalCount > 0 ? Math.round((clearedCount / totalCount) * 100) : 0;

  const riskSummary = useMemo(() => {
    const critical = projBlockers.filter(b => b.severity === 'critical').length;
    const overdue = projBlockers.filter(b => new Date(b.dueDate?.split('/').reverse().join('-')) < new Date()).length;
    const tradeHolds = projTrades.filter(t => t.status === 'hold').length;
    return { critical, overdue, tradeHolds };
  }, [projBlockers, projTrades]);

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
      toast.success('Dossier generated', { description: `Pre-Slab readiness dossier for ${proj?.name}` });
    }, 2000);
  };

  const handleSendToSurveyor = () => {
    toast.success('Sent to surveyor', { description: `Readiness pack for ${proj?.name} sent to Mark Stevens` });
  };

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="readiness-dossier-page">
        {/* Dossier cover header - PDF style */}
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <div className="bg-slate-900 px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">easypermit Readiness Dossier</span>
                  {generated && <Badge className="bg-emerald-500/20 text-emerald-300 border-0 text-[9px]">Generated</Badge>}
                </div>
                <h2 className="text-xl font-bold text-white">{proj?.name}</h2>
                <p className="text-sm text-slate-400 mt-0.5">{proj?.address} · Permit: {proj?.permitNumber}</p>
              </div>
              <div className="flex gap-2">
                {/* Project selector */}
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                  className="bg-slate-800 border border-slate-600 text-slate-300 text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="dossier-project-selector">
                  {projects.filter(p => p.status !== 'complete').map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 gap-1.5 text-xs" onClick={() => setShowPreview(true)} data-testid="dossier-preview-btn">
                  <Eye className="w-3.5 h-3.5" />Preview
                </Button>
                <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 gap-1.5 text-xs" data-testid="dossier-print-btn">
                  <Printer className="w-3.5 h-3.5" />Print
                </Button>
                <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 gap-1.5 text-xs" data-testid="dossier-export-btn">
                  <Download className="w-3.5 h-3.5" />Export PDF
                </Button>
                {generated ? (
                  <Button size="sm" onClick={handleSendToSurveyor} className="bg-emerald-600 hover:bg-emerald-500 gap-1.5 text-xs" data-testid="dossier-send-btn">
                    <Send className="w-3.5 h-3.5" />Send to surveyor
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleGenerate} disabled={generating} className="bg-blue-600 hover:bg-blue-500 gap-1.5 text-xs" data-testid="dossier-generate-btn">
                    {generating ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                    {generating ? 'Generating...' : 'Generate dossier'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Summary stats */}
          <div className="px-6 py-4 grid grid-cols-5 gap-3" data-testid="dossier-summary-stats">
            {[
              { label: 'Stage', value: 'Pre-Slab', sub: 'Slab Pour', icon: Building2, color: 'text-slate-700' },
              { label: 'Readiness', value: `${readinessPercent}%`, sub: `${clearedCount}/${totalCount} cleared`, icon: Target, color: readinessPercent === 100 ? 'text-emerald-700' : 'text-amber-700' },
              { label: 'Blockers', value: `${projBlockers.filter(b => b.stage === 'Pre-Slab').length}`, sub: `${riskSummary.critical} critical`, icon: AlertTriangle, color: riskSummary.critical > 0 ? 'text-red-700' : 'text-emerald-700' },
              { label: 'Evidence', value: `${approvedEvidence.length}`, sub: `${pendingEvidence.length} pending`, icon: Eye, color: 'text-blue-700' },
              { label: 'Trade', value: projTrades.find(t => t.id === 'trade-001')?.status === 'released' ? 'Released' : 'On Hold', sub: 'Concreter', icon: Truck, color: projTrades.find(t => t.id === 'trade-001')?.status === 'released' ? 'text-emerald-700' : 'text-red-700' },
            ].map(s => (
              <div key={s.label} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <s.icon className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
                </div>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-slate-500">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Readiness progress bar */}
        <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid="dossier-readiness-bar">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-900">Stage Readiness Progress</span>
            <span className={`text-sm font-bold ${readinessPercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>{readinessPercent}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${readinessPercent === 100 ? 'bg-emerald-500' : readinessPercent > 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
              style={{ width: `${readinessPercent}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-slate-400">{clearedCount} conditions cleared</span>
            <span className="text-[10px] text-slate-400">{totalCount - clearedCount} remaining</span>
          </div>
        </div>

        {/* Conditions checklist */}
        <DossierSection title="Pre-Slab Conditions" icon={Lock}
          badge={<Badge className={`text-[10px] ${clearedCount === totalCount ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{clearedCount}/{totalCount}</Badge>}>
          <div className="space-y-2">
            {projConditions.map((c, i) => (
              <div key={c.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${c.status === 'cleared' ? 'bg-emerald-50/50 border-emerald-100' : c.isBlocker ? 'bg-red-50/50 border-red-100' : 'bg-white border-slate-100'}`}
                data-testid={`dossier-condition-${c.id}`}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold border-2"
                  style={{ borderColor: c.status === 'cleared' ? '#10b981' : c.isBlocker ? '#ef4444' : '#94a3b8', color: c.status === 'cleared' ? '#10b981' : c.isBlocker ? '#ef4444' : '#64748b' }}>
                  {c.status === 'cleared' ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800">{c.number} — {c.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-400 flex items-center gap-1"><User className="w-2.5 h-2.5" />{c.owner}</span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Due {c.dueDate}</span>
                  </div>
                </div>
                <Badge className={`text-[10px] ${c.status === 'cleared' ? 'bg-emerald-100 text-emerald-700' : c.isBlocker ? 'bg-red-100 text-red-700' : c.status === 'in-progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                  {c.status === 'cleared' ? 'Cleared' : c.status === 'in-progress' ? 'In Progress' : c.isBlocker ? 'Blocker' : 'Pending'}
                </Badge>
              </div>
            ))}
          </div>
        </DossierSection>

        {/* Evidence pack */}
        <DossierSection title="Evidence Pack" icon={Eye}
          badge={<Badge className="text-[10px] bg-blue-100 text-blue-700">{approvedEvidence.length + pendingEvidence.length} files</Badge>}>
          {approvedEvidence.length > 0 && (
            <div className="space-y-1.5 mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-2">Approved</p>
              {approvedEvidence.map(e => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800">{e.fileName}</p>
                    <p className="text-[10px] text-slate-500">{e.uploadDate} · {e.uploadedBy} · {e.fileSize}</p>
                  </div>
                  <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Approved</Badge>
                </div>
              ))}
            </div>
          )}
          {pendingEvidence.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-2">Pending review</p>
              {pendingEvidence.map(e => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-amber-50/50 border border-amber-100">
                  <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800">{e.fileName}</p>
                    <p className="text-[10px] text-slate-500">Match confidence: {e.matchConfidence}% · {e.fileSize}</p>
                  </div>
                  <Badge className="text-[9px] bg-amber-100 text-amber-700">Pending</Badge>
                </div>
              ))}
            </div>
          )}
          {approvedEvidence.length === 0 && pendingEvidence.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">No evidence uploaded for this stage yet.</p>
          )}
        </DossierSection>

        {/* Trade Work Proofs — fed from signed-off trades */}
        {(() => {
          const projWps = workProofs.filter(w => w.projectId === selectedProject);
          const approvedWps = projWps.filter(w => w.status === 'approved');
          const pendingWps = projWps.filter(w => w.status === 'pending');
          if (projWps.length === 0) return null;
          return (
            <DossierSection title="Trade Work Proofs" icon={Truck}
              badge={<Badge className="text-[10px] bg-teal-100 text-teal-700">{approvedWps.length} approved / {projWps.length} total</Badge>}>
              <div className="space-y-2">
                {approvedWps.map(wp => (
                  <div key={wp.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100" data-testid={`dossier-wp-${wp.id}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800">{wp.trade} — {wp.fileName}</p>
                      <p className="text-[10px] text-slate-500">{wp.uploadedBy} · {wp.uploadDate} · Reviewed by {wp.reviewedBy}</p>
                      {wp.notes && <p className="text-[10px] text-slate-600 italic mt-0.5">{wp.notes}</p>}
                    </div>
                    <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Approved</Badge>
                  </div>
                ))}
                {pendingWps.map(wp => (
                  <div key={wp.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-amber-50/50 border border-amber-100" data-testid={`dossier-wp-${wp.id}`}>
                    <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800">{wp.trade} — {wp.fileName}</p>
                      <p className="text-[10px] text-slate-500">{wp.uploadedBy} · {wp.uploadDate}</p>
                    </div>
                    <Badge className="text-[9px] bg-amber-100 text-amber-700">Pending Review</Badge>
                  </div>
                ))}
              </div>
            </DossierSection>
          );
        })()}

        {/* Open blockers */}
        {projBlockers.length > 0 && (
          <DossierSection title="Open Blockers" icon={AlertTriangle}
            badge={<Badge className="text-[10px] bg-red-100 text-red-700">{projBlockers.length} active</Badge>}>
            <div className="space-y-2">
              {projBlockers.map(b => (
                <div key={b.id} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50/50 border border-red-100">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800">{b.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-500">{b.owner} ({b.ownerRole})</span>
                      <span className="text-[10px] text-slate-400">· Due: {b.dueDate}</span>
                      {b.chaseCount > 0 && <span className="text-[10px] text-blue-500">· Chased {b.chaseCount}x</span>}
                    </div>
                    {b.readinessImpact && <p className="text-[10px] text-red-600 mt-1">{b.readinessImpact}</p>}
                  </div>
                  <Badge className={`text-[10px] ${b.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{b.severity}</Badge>
                </div>
              ))}
            </div>
          </DossierSection>
        )}

        {/* Trade status */}
        <DossierSection title="Trade Release Status" icon={Truck}
          badge={<Badge className="text-[10px] bg-slate-100 text-slate-600">{projTrades.length} trades</Badge>}>
          <div className="space-y-2">
            {projTrades.map(t => (
              <div key={t.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${t.status === 'released' ? 'bg-emerald-50/50 border-emerald-100' : t.status === 'ready' ? 'bg-blue-50/50 border-blue-100' : 'bg-amber-50/50 border-amber-100'}`}>
                <Truck className={`w-4 h-4 shrink-0 ${t.status === 'released' ? 'text-emerald-600' : t.status === 'ready' ? 'text-blue-600' : 'text-amber-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800">{t.trade} — {t.company}</p>
                  <p className="text-[10px] text-slate-500">{t.project} · {t.stage}</p>
                  {t.blockersRemaining > 0 && <p className="text-[10px] text-red-600 mt-0.5">{t.blockersRemaining} blocker(s) remaining</p>}
                </div>
                <Badge className={`text-[10px] ${t.status === 'released' ? 'bg-emerald-100 text-emerald-700' : t.status === 'ready' ? 'bg-blue-100 text-blue-700' : t.status === 'hold' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                  {t.status === 'hold' ? 'On Hold' : t.status === 'ready' ? 'Ready' : t.status === 'released' ? 'Released' : t.status}
                </Badge>
              </div>
            ))}
          </div>
        </DossierSection>

        {/* Upcoming milestones */}
        {projCalendar.length > 0 && (
          <DossierSection title="Upcoming Milestones" icon={Calendar}
            badge={<Badge className="text-[10px] bg-slate-100 text-slate-600">{projCalendar.length} events</Badge>}>
            <div className="space-y-1.5">
              {projCalendar.slice(0, 5).map(ev => (
                <div key={ev.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                  <div className={`w-2 h-2 rounded-full ${ev.type === 'trade-release' ? 'bg-emerald-500' : ev.type === 'inspection' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800">{ev.title}</p>
                    <p className="text-[10px] text-slate-400">{ev.date}</p>
                  </div>
                  <Badge className="text-[9px] bg-slate-100 text-slate-500">{ev.type?.replace('-', ' ')}</Badge>
                </div>
              ))}
            </div>
          </DossierSection>
        )}

        {/* AI Readiness Assessment */}
        <div className="border border-blue-200 rounded-xl overflow-hidden bg-blue-50/30" data-testid="dossier-ai-assessment">
          <div className="bg-blue-50 px-5 py-3 flex items-center gap-2 border-b border-blue-200">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-blue-900">AI Readiness Assessment</h3>
          </div>
          <div className="p-5 space-y-3">
            <div className="bg-white rounded-lg border border-blue-100 p-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                {readinessPercent === 100
                  ? `All ${totalCount} Pre-Slab conditions for ${proj?.name} have been cleared. Evidence pack is complete with ${approvedEvidence.length} approved items. All trades are eligible for release. Recommend proceeding with surveyor notification.`
                  : `${totalCount - clearedCount} of ${totalCount} Pre-Slab conditions remain uncleared for ${proj?.name}. ${riskSummary.critical} critical blocker(s) require immediate attention. ${riskSummary.tradeHolds} trade(s) on hold pending resolution. Recommend chasing ${projBlockers[0]?.owner || 'owners'} for outstanding items before scheduling surveyor.`
                }
              </p>
            </div>
            {readinessPercent < 100 && projBlockers.length > 0 && (
              <div className="bg-white rounded-lg border border-blue-100 p-4">
                <p className="text-xs font-semibold text-slate-700 mb-2">Recommended next actions:</p>
                <div className="space-y-1.5">
                  {projBlockers.slice(0, 3).map((b, i) => (
                    <div key={b.id} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                      Chase {b.owner} for {b.title}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-3 border-t border-slate-100">
          <p className="text-[10px] text-slate-400">Generated by easypermit · {workspace.name} · Guidance only — review before submitting to surveyor</p>
        </div>
      </div>
      <PackPreviewModal open={showPreview} onClose={() => setShowPreview(false)} type="readiness" data={{ projectName: proj?.name, address: proj?.address, permit: proj?.permit, workspace: workspace.name, readiness: `${readinessPercent}%`, blockers: String(projBlockers.length), evidence: `${approvedEvidence.length + pendingEvidence.length} files` }} />
    </AppLayout>
  );
}
